import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, BellOff, Pause, Play } from "lucide-react";

const TAGS = [
  "AI",
  "Capital",
  "Climate",
  "Craft",
  "Design",
  "Founders",
  "Policy",
  "Research",
  "Scale",
];

// Static sponsor registry entries
const STATIC_ENTRIES = {
  "demo-techcorp-token": {
    company: "TechCorp",
    email: "partnerships@techcorp.ai",
    status: "active",
    interests: ["AI", "Scale", "Founders"],
    paused_until: null,
  },
  "demo-greentide-token": {
    company: "Greentide",
    email: "invest@greentide.io",
    status: "active",
    interests: ["Climate", "Capital"],
    paused_until: null,
  },
  "demo-paused-token": {
    company: "Paused Corp",
    email: "hello@pausedcorp.com",
    status: "paused",
    interests: ["Design", "Research"],
    paused_until: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString(), // 14 days from now
  },
  "demo-unsubscribed-token": {
    company: "Unsubscribed Inc",
    email: "contact@unsubscribed.com",
    status: "unsubscribed",
    interests: [],
    paused_until: null,
  },
};

export default function SponsorPreferences() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [interests, setInterests] = useState([]);

  useEffect(() => {
    if (!token) {
      setError("Missing token.");
      setLoading(false);
      return;
    }

    // Simulate API delay
    setTimeout(() => {
      const foundEntry = STATIC_ENTRIES[token];

      if (foundEntry) {
        setEntry(foundEntry);
        setInterests(foundEntry.interests || []);
      } else {
        setError("This link is invalid or has expired.");
      }
      setLoading(false);
    }, 600);
  }, [token]);

  const toggle = (t) =>
    setInterests((i) => (i.includes(t) ? i.filter((x) => x !== t) : [...i, t]));

  const savePrefs = async () => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const updatedEntry = { ...entry, interests };
      setEntry(updatedEntry);
      toast.success("Preferences saved.");
    } catch (err) {
      toast.error("Save failed.");
    }
  };

  const pause = async (days) => {
    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const updatedEntry = {
        ...entry,
        status: days === 0 ? "active" : "paused",
        paused_until:
          days === 0
            ? null
            : new Date(Date.now() + days * 24 * 60 * 60 * 1000).toISOString(),
      };
      setEntry(updatedEntry);
      toast.success(
        days === 0 ? "Resumed notifications." : `Paused for ${days} days.`,
      );
    } catch (err) {
      toast.error("Update failed.");
    }
  };

  const unsubscribe = async () => {
    if (!confirm("Unsubscribe from all sponsor opportunity emails?")) return;

    // Simulate API delay
    await new Promise((resolve) => setTimeout(resolve, 500));

    try {
      const updatedEntry = { ...entry, status: "unsubscribed", interests: [] };
      setEntry(updatedEntry);
      setInterests([]);
      toast.success("You've been unsubscribed.");
    } catch (err) {
      toast.error("Unsubscribe failed.");
    }
  };

  if (loading) {
    return (
      <div
        className="container-x py-32 text-white/70"
        data-testid="prefs-loading"
      >
        Loading your preferences…
      </div>
    );
  }

  if (error || !entry) {
    return (
      <div
        className="container-x py-24 md:py-32 max-w-xl"
        data-testid="prefs-invalid"
      >
        <p className="overline mb-3 text-red-300/80">Invalid link</p>
        <h1 className="font-display font-light text-3xl md:text-4xl tracking-tighter leading-[1] mb-6">
          {error || "We couldn't find your entry."}
        </h1>
        <p className="text-white/75 leading-relaxed mb-8">
          Use the link in any vmeetio sponsor opportunity email. If you've
          already unsubscribed, no further action is needed.
        </p>
        <Link to="/" className="btn-ghost">
          Back to vmeetio
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="sponsor-preferences-page" className="min-h-screen">
      <section
        className="border-b border-white/[0.06]"
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
      >
        <div className="container-x py-14">
          <Link
            to="/"
            className="overline text-white/70 hover:text-white inline-flex items-center gap-2 mb-5"
          >
            <ArrowLeft size={12} /> Back to vmeetio
          </Link>
          <p className="overline mb-3">Sponsor email preferences</p>
          <h1 className="font-display font-light text-3xl md:text-5xl tracking-tighter leading-[0.95] mb-4">
            {entry.company}
          </h1>
          <p className="text-sm text-white/70">{entry.email}</p>
          <div className="mt-4">
            <StatusBadge
              status={entry.status}
              pausedUntil={entry.paused_until}
            />
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x grid md:grid-cols-12 gap-8">
          <div className="md:col-span-7 space-y-5 glass rounded-2xl p-7">
            <p className="overline mb-2">
              Pick the topics you want to hear about
            </p>
            <div className="flex flex-wrap gap-2">
              {TAGS.map((t) => (
                <button
                  key={t}
                  type="button"
                  onClick={() => toggle(t)}
                  data-testid={`prefs-tag-${t}`}
                  className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${
                    interests.includes(t)
                      ? "bg-[#C6A87C] text-[#061412] border-[#C6A87C]"
                      : "border-white/15 text-white/75 hover:border-[#C6A87C]/50 hover:text-[#C6A87C]"
                  }`}
                >
                  {t}
                </button>
              ))}
            </div>
            <p className="text-[11px] text-white/55">
              Empty = notify about every event regardless of topic.
            </p>
            <button
              onClick={savePrefs}
              className="btn-gold mt-3"
              data-testid="prefs-save"
            >
              <CheckCircle2 size={14} /> Save preferences
            </button>
          </div>

          <aside className="md:col-span-5 space-y-4">
            <div className="glass rounded-2xl p-6">
              <Pause className="text-[#C6A87C] mb-3" size={18} />
              <h3 className="font-display text-xl tracking-tight mb-2">
                Take a break
              </h3>
              <p className="text-sm text-white/75 mb-4">
                Pause all sponsor opportunity emails. You can come back any
                time.
              </p>
              <div className="flex flex-wrap gap-2">
                {[7, 30, 90].map((d) => (
                  <button
                    key={d}
                    onClick={() => pause(d)}
                    data-testid={`prefs-pause-${d}`}
                    className="px-3 py-1.5 rounded-full text-xs border border-white/15 text-white/80 hover:border-[#C6A87C]/50 hover:text-[#C6A87C] transition-colors"
                  >
                    Pause {d} days
                  </button>
                ))}
                <button
                  onClick={() => pause(0)}
                  data-testid="prefs-resume"
                  className="px-3 py-1.5 rounded-full text-xs border border-[#2A7E5D]/40 bg-[#2A7E5D]/15 text-[#7BC8A6] hover:bg-[#2A7E5D]/25 transition-colors flex items-center gap-1.5"
                >
                  <Play size={11} /> Resume now
                </button>
              </div>
            </div>

            <div className="glass rounded-2xl p-6">
              <BellOff className="text-red-300/80 mb-3" size={18} />
              <h3 className="font-display text-xl tracking-tight mb-2">
                Unsubscribe completely
              </h3>
              <p className="text-sm text-white/75 mb-4">
                Stop all sponsor opportunity emails. You can rejoin from the
                registry page any time.
              </p>
              <button
                onClick={unsubscribe}
                data-testid="prefs-unsubscribe"
                className="px-4 py-2 rounded-full text-xs border border-red-300/30 text-red-300/90 hover:bg-red-300/10 transition-colors"
              >
                Unsubscribe
              </button>
            </div>
          </aside>
        </div>
      </section>
    </div>
  );
}

function StatusBadge({ status, pausedUntil }) {
  const map = {
    active: {
      label: "Active — receiving notifications",
      cls: "bg-[#2A7E5D]/15 border-[#2A7E5D]/40 text-[#7BC8A6]",
    },
    paused: {
      label: pausedUntil
        ? `Paused until ${new Date(pausedUntil).toLocaleDateString()}`
        : "Paused",
      cls: "bg-[#C6A87C]/15 border-[#C6A87C]/40 text-[#C6A87C]",
    },
    unsubscribed: {
      label: "Unsubscribed — no emails will be sent",
      cls: "bg-white/[0.04] border-white/15 text-white/60",
    },
  };
  const m = map[status] || map.active;
  return (
    <span
      className={`inline-flex items-center px-3 py-1 rounded-full border text-xs ${m.cls}`}
      data-testid="prefs-status"
    >
      {m.label}
    </span>
  );
}
